import { useEffect, useState } from "react";

import {
  IAdminPatientDetail,
  IAdminUserDetail,
} from "../../../../@types/admin/AdminPatient";
import { useUpdatePatientUser } from "../../../../hooks/mutations/admin/patients";
import { useModal } from "../../../../hooks/useModal";
import { useToast } from "../../../../hooks/useToast";
import { getLongFormattedDate } from "../../../../utils/dateFormatter";
import { errorToastMessage } from "../../../../utils/HttpError";
import { passwordSetTone } from "../../Patients/patientListFields";
import {
  CheckboxField,
  FieldError,
  FieldHint,
  FilterButton,
  FilterGroup,
  FilterInput,
  FilterLabel,
  FormGrid,
  ReadOnlyValue,
  SectionActions,
  SectionCaption,
  SectionCard,
  SectionHeader,
  SectionHeading,
  SectionTitle,
  StatusBadge,
} from "../AdminPage.styles";
import { ChangePasswordModal } from "./ChangePasswordModal";
import { SendEnrolmentEmailModal } from "./SendEnrolmentEmailModal";

interface IPatientUserFormProps {
  patient: IAdminPatientDetail;
  user: IAdminUserDetail;
}

interface IUserFormState {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  isTestAccount: boolean;
}

function toFormState(user: IAdminUserDetail): IUserFormState {
  return {
    firstName: user.firstName ?? "",
    lastName: user.lastName ?? "",
    email: user.email ?? "",
    phoneNumber: user.phoneNumber ?? "",
    isTestAccount: user.isTestAccount,
  };
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * The linked login half of the detail panel — Retool's `formUserDetails`
 * (EN-5169).
 *
 * Unlike the Staff version of this form (EN-5150), email IS editable here, as
 * it was in Retool — a patient's login address is routinely corrected. Changing
 * it is what triggers the enrolment-email prompt below.
 *
 * ⚠️ **Not for an archived patient** (EN-5171). Three of the things this form
 * does are credential or enrolment operations, and none of them mean anything
 * for a discharged patient: changing the sign-in address, re-sending the
 * enrolment email, and resetting the password. Retool reached the same
 * conclusion — `page_ArchivedPatients` has no `modalChangePassword` and no
 * `sendEnrolmentModal`, and its user PUT body drops `email` and
 * `sendEnrolmentEmail` entirely.
 *
 * The rule keys off `patient.archived`, NOT off which list the operator came
 * from. `GET /Retool/Patients` does not filter archived, so an archived patient
 * is reachable from the live list too, and a page-scoped rule would have let
 * that path reset their password.
 *
 * Name, phone and the test-account flag stay editable: correcting a misspelled
 * name on a discharged record is routine and touches nothing the patient can
 * still use.
 */
export function PatientUserForm({ patient, user }: IPatientUserFormProps) {
  const { showToast } = useToast();
  const { openModal, closeModal } = useModal();
  const { updateUser, isUpdatingUser } = useUpdatePatientUser();

  const [form, setForm] = useState<IUserFormState>(() => toFormState(user));

  useEffect(() => {
    setForm(toFormState(user));
  }, [user]);

  const emailError = (() => {
    const email = form.email.trim();
    if (!email) return "Email is required.";
    if (!EMAIL_PATTERN.test(email)) {
      return "That doesn't look like an email address.";
    }
    return undefined;
  })();

  const errors = {
    firstName: form.firstName.trim() ? undefined : "First name is required.",
    lastName: form.lastName.trim() ? undefined : "Last name is required.",
    email: emailError,
  };
  const hasErrors = Object.values(errors).some(Boolean);

  const emailChanged = (user.email ?? "") !== form.email.trim();

  // See the note above: credential and enrolment actions are unavailable on a
  // discharged patient.
  const isArchived = patient.archived;

  const setField = <TKey extends keyof IUserFormState>(
    key: TKey,
    value: IUserFormState[TKey]
  ) => setForm((previous) => ({ ...previous, [key]: value }));

  const save = async (sendEnrolmentEmail: boolean) => {
    try {
      await updateUser({
        userId: user.id,
        patientId: Number(patient.id),
        request: {
          id: user.id,
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          // The stored address for an archived patient, never the form's — the
          // field is read-only there, and sending it back is what makes the
          // read-only-ness real rather than cosmetic.
          email: isArchived ? user.email : form.email.trim(),
          phoneNumber: form.phoneNumber.trim() || null,
          isTestAccount: form.isTestAccount,
          sendEnrolmentEmail: isArchived ? false : sendEnrolmentEmail,
        },
      });
      showToast("The user details were updated successfully.", "success");
      return true;
    } catch (error) {
      // PUT /Retool/Users rejects a duplicate email with a message naming the
      // conflict. Retool surfaced it through a queryFailureConditions rule
      // reading data.errors[0].message; `ensureOk` puts it on the error.
      showToast(
        errorToastMessage(
          error,
          "There was an error updating the user details."
        ),
        "warning"
      );
      return false;
    }
  };

  const handleSave = () => {
    if (hasErrors) return;

    // Retool asked about the enrolment email ONLY when the address changed
    // (the form's two submit events were gated on
    // `getUserDetails.data.email !== formUserDetails.data.email`). A patient
    // whose login address moves may need the enrolment mail sent to the new
    // one — and may equally not, if the address was simply mistyped, which is
    // why both answers save.
    // An archived patient cannot change email, so the prompt is unreachable —
    // guarded explicitly rather than relying on emailChanged staying false.
    if (isArchived || !emailChanged) {
      save(false);
      return;
    }

    openModal(
      <SendEnrolmentEmailModal
        newEmail={form.email.trim()}
        isSaving={isUpdatingUser}
        onAnswer={async (sendEnrolmentEmail) => {
          const saved = await save(sendEnrolmentEmail);
          if (saved) closeModal();
        }}
        onCancel={closeModal}
      />,
      { width: "520px", showCloseButton: true }
    );
  };

  const handleChangePassword = () => {
    openModal(<ChangePasswordModal user={user} onDone={closeModal} />, {
      width: "520px",
      showCloseButton: true,
    });
  };

  return (
    <SectionCard>
      <SectionHeader>
        <SectionHeading>
          <SectionTitle>User account</SectionTitle>
          <SectionCaption>User ID {user.id}</SectionCaption>
        </SectionHeading>
      </SectionHeader>

      <FormGrid>
        <FilterGroup>
          <FilterLabel htmlFor="patient-user-first-name">
            First name
          </FilterLabel>
          <FilterInput
            id="patient-user-first-name"
            type="text"
            value={form.firstName}
            onChange={(e) => setField("firstName", e.target.value)}
          />
          {errors.firstName && <FieldError>{errors.firstName}</FieldError>}
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="patient-user-last-name">Last name</FilterLabel>
          <FilterInput
            id="patient-user-last-name"
            type="text"
            value={form.lastName}
            onChange={(e) => setField("lastName", e.target.value)}
          />
          {errors.lastName && <FieldError>{errors.lastName}</FieldError>}
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="patient-user-email">Email</FilterLabel>
          {isArchived ? (
            <>
              <ReadOnlyValue>{user.email || "-"}</ReadOnlyValue>
              <FieldHint>
                The sign-in address can&apos;t be changed on an archived
                patient. Un-archive them first.
              </FieldHint>
            </>
          ) : (
            <>
              <FilterInput
                id="patient-user-email"
                type="email"
                value={form.email}
                onChange={(e) => setField("email", e.target.value)}
              />
              {errors.email && <FieldError>{errors.email}</FieldError>}
              {!errors.email && emailChanged && (
                <FieldHint>
                  This is the address the patient signs in with. Saving will ask
                  whether to send the enrolment email to it.
                </FieldHint>
              )}
            </>
          )}
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="patient-user-phone">Phone number</FilterLabel>
          <FilterInput
            id="patient-user-phone"
            type="tel"
            value={form.phoneNumber}
            onChange={(e) => setField("phoneNumber", e.target.value)}
          />
        </FilterGroup>

        <FilterGroup>
          <FilterLabel>Password set</FilterLabel>
          <ReadOnlyValue>
            <StatusBadge status={passwordSetTone(user.passwordSet)}>
              {user.passwordSet || "-"}
            </StatusBadge>
          </ReadOnlyValue>
        </FilterGroup>

        <FilterGroup>
          <FilterLabel>Last used</FilterLabel>
          <ReadOnlyValue>
            {getLongFormattedDate(user.lastUsed, "Never")}
          </ReadOnlyValue>
        </FilterGroup>

        <FilterGroup>
          <FilterLabel>Enrolment email sent</FilterLabel>
          <ReadOnlyValue>
            {getLongFormattedDate(user.enrolmentEmailSent, "Never")}
          </ReadOnlyValue>
        </FilterGroup>

        <FilterGroup>
          <FilterLabel>First login</FilterLabel>
          <ReadOnlyValue>
            {getLongFormattedDate(user.firstLogin, "Never")}
          </ReadOnlyValue>
        </FilterGroup>

        <FilterGroup span="full">
          <CheckboxField>
            <input
              type="checkbox"
              checked={form.isTestAccount}
              onChange={(e) => setField("isTestAccount", e.target.checked)}
            />
            Test account
          </CheckboxField>
        </FilterGroup>
      </FormGrid>

      <SectionActions>
        {/* Retool put this button inside the form body, where it read as a
            field. It is an action on the account, so it belongs in the action
            row beside Save — and it is absent entirely for an archived patient,
            as it was on Retool's archived page. */}
        {!isArchived && (
          <FilterButton variant="secondary" onClick={handleChangePassword}>
            Change password
          </FilterButton>
        )}
        <FilterButton
          variant="primary"
          disabled={isUpdatingUser || hasErrors}
          onClick={handleSave}
        >
          {isUpdatingUser ? "Saving..." : "Save user account"}
        </FilterButton>
      </SectionActions>
    </SectionCard>
  );
}
